// src/pages/GenerarDECA.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast/useToast.jsx";
import { useAuth } from "../contexts/AuthContext";
import { db, serverTimestamp } from "../firebase";
import { doc, setDoc, collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { v4 as uuid } from "uuid";
import DatePicker from "../components/DatePicker";

export default function GenerarDECA() {
  const { showToast } = useToast();
  const { user, companyId } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // DATOS DEL CARGADOR (quien envía)
    cargadorNombre: "",
    cargadorCIF: "",
    cargadorDireccion: "",
    cargadorCiudad: "",
    cargadorCP: "",
    cargadorTelefono: "",
    cargadorEmail: "",
    
    // DATOS DEL TRANSPORTISTA
    transportistaNombre: "",
    transportistaCIF: "",
    transportistaDireccion: "",
    transportistaCiudad: "",
    transportistaCP: "",
    transportistaTelefono: "",
    transportistaEmail: "",
    transportistaLicencia: "",
    
    // DATOS DE ORIGEN Y DESTINO
    origenDireccion: "",
    origenCiudad: "",
    origenCP: "",
    destinoDireccion: "",
    destinoCiudad: "",
    destinoCP: "",
    
    // DATOS DEL TRANSPORTE
    fechaRecogida: "",
    horaRecogida: "",
    fechaEntrega: "",
    horaEntrega: "",
    
    // DATOS DE LA MERCANCÍA
    descripcionMercancia: "",
    peso: "",
    volumen: "",
    bultos: "",
    observacionesMercancia: "",
  });
  
  const [vehiculoData, setVehiculoData] = useState({
    matricula: "",
    marca: "",
    modelo: "",
    conductorNombre: "",
    conductorDNI: "",
    conductorTelefono: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Auto-save form data to localStorage
      try {
        localStorage.setItem('deca_form_data', JSON.stringify({
          formData: newData,
          vehiculoData,
          timestamp: new Date().toISOString()
        }));
      } catch (error) {
        console.warn('Error saving form data:', error);
      }
      
      return newData;
    });
  };

  const handleVehiculoChange = (e) => {
    const { name, value } = e.target;
    setVehiculoData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // Auto-save form data to localStorage
      try {
        localStorage.setItem('deca_form_data', JSON.stringify({
          formData,
          vehiculoData: newData,
          timestamp: new Date().toISOString()
        }));
      } catch (error) {
        console.warn('Error saving form data:', error);
      }
      
      return newData;
    });
  };

  const generarCodigoDECA = async () => {
    try {
      // Obtener el último número de DECA para la empresa
      const q = query(
        collection(db, 'decas'),
        where('companyId', '==', companyId),
        orderBy('numeroSecuencial', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      let siguienteNumero = 1;
      
      if (!snapshot.empty) {
        const ultimoDeca = snapshot.docs[0].data();
        siguienteNumero = (ultimoDeca.numeroSecuencial || 0) + 1;
      }
      
      // Formato: DECA-CEJM-0001
      const numeroFormateado = siguienteNumero.toString().padStart(4, '0');
      return {
        codigo: `DECA-CEJM-${numeroFormateado}`,
        numeroSecuencial: siguienteNumero
      };
    } catch (error) {
      console.error('Error generando código secuencial:', error);
      // Fallback al sistema anterior si hay error
      const timestamp = Date.now().toString(36);
      const random = Math.random().toString(36).substring(2, 6);
      return {
        codigo: `DECA-CEJM-${timestamp}-${random}`.toUpperCase(),
        numeroSecuencial: Date.now()
      };
    }
  };

  // Load saved form data on component mount
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const saved = localStorage.getItem('deca_form_data');
        if (saved) {
          const { formData: savedFormData, vehiculoData: savedVehiculoData, timestamp } = JSON.parse(saved);
          
          // Only restore if saved within last 24 hours
          const saveAge = Date.now() - new Date(timestamp).getTime();
          const twentyFourHours = 24 * 60 * 60 * 1000;
          
          if (saveAge < twentyFourHours) {
            console.log('📋 Restaurando datos del formulario DECA...');
            setFormData(savedFormData);
            setVehiculoData(savedVehiculoData);
            
            showToast('Datos del formulario restaurados automáticamente', 'success');
          } else {
            // Clear old data
            localStorage.removeItem('deca_form_data');
            console.log('🗑️ Datos antiguos del formulario eliminados');
          }
        }
      } catch (error) {
        console.error('Error loading saved form data:', error);
        localStorage.removeItem('deca_form_data');
      }
    };
    
    loadSavedData();
  }, []);
  const handleClearForm = () => {
    // Clear all form data
    setFormData({
      cargadorNombre: "",
      cargadorCIF: "",
      cargadorDireccion: "",
      cargadorCiudad: "",
      cargadorCP: "",
      cargadorTelefono: "",
      cargadorEmail: "",
      
      transportistaNombre: "",
      transportistaCIF: "",
      transportistaDireccion: "",
      transportistaCiudad: "",
      transportistaCP: "",
      transportistaTelefono: "",
      transportistaEmail: "",
      transportistaLicencia: "",
      
      origenDireccion: "",
      origenCiudad: "",
      origenCP: "",
      destinoDireccion: "",
      destinoCiudad: "",
      destinoCP: "",
      
      fechaRecogida: "",
      horaRecogida: "",
      fechaEntrega: "",
      horaEntrega: "",
      
      descripcionMercancia: "",
      peso: "",
      volumen: "",
      bultos: "",
      observacionesMercancia: "",
    });
    
    setVehiculoData({
      matricula: "",
      marca: "",
      modelo: "",
      conductorNombre: "",
      conductorDNI: "",
      conductorTelefono: "",
    });
    
    // Clear localStorage
    try {
      localStorage.removeItem('deca_form_data');
      console.log('🗑️ Formulario limpiado manualmente');
    } catch (error) {
      console.warn('Error clearing localStorage:', error);
    }
    
    showToast('Formulario limpiado correctamente', 'success');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validaciones básicas
      if (!formData.cargadorNombre || !formData.transportistaNombre) {
        throw new Error('Los datos del cargador y transportista son obligatorios');
      }
      if (!formData.origenDireccion || !formData.destinoDireccion) {
        throw new Error('Las direcciones de origen y destino son obligatorias');
      }
      if (!formData.descripcionMercancia) {
        throw new Error('La descripción de la mercancía es obligatoria');
      }
      if (!vehiculoData.matricula || !vehiculoData.conductorNombre) {
        throw new Error('Los datos del vehículo y conductor son obligatorios');
      }

      // Verificar autenticación y obtener claims actualizados
      if (!user) {
        showToast('Debes iniciar sesión para generar documentos DECA', 'error');
        navigate('/login');
        return;
      }

      // Forzar actualización de custom claims si están vacíos
      let effectiveCompanyId = companyId;
      if (!effectiveCompanyId) {
        console.log('⚠️ CompanyId no encontrado, refrescando token...');
        try {
          await user.getIdToken(true); // Force refresh
          const tokenResult = await user.getIdTokenResult();
          effectiveCompanyId = tokenResult.claims.companyId || 'cejm'; // Fallback to CEJM
          console.log('🔄 CompanyId obtenido tras refresh:', effectiveCompanyId);
        } catch (claimsError) {
          console.error('Error obteniendo claims:', claimsError);
          effectiveCompanyId = 'cejm'; // Default fallback
        }
      }

      const codigoInfo = await generarCodigoDECA();
      const decaId = uuid();

      // Crear documento DECA
      const decaData = {
        id: decaId,
        codigoDECA: codigoInfo.codigo,
        numeroSecuencial: codigoInfo.numeroSecuencial,
        companyId: effectiveCompanyId,
        createdBy: user?.uid || 'sistema',
        createdByEmail: user?.email || 'sistema@cejm.eu',
        createdAt: serverTimestamp(),
        
        // Datos del formulario
        cargador: {
          nombre: formData.cargadorNombre,
          cif: formData.cargadorCIF,
          direccion: formData.cargadorDireccion,
          ciudad: formData.cargadorCiudad,
          codigoPostal: formData.cargadorCP,
          telefono: formData.cargadorTelefono,
          email: formData.cargadorEmail
        },
        transportista: {
          nombre: formData.transportistaNombre,
          cif: formData.transportistaCIF,
          direccion: formData.transportistaDireccion,
          ciudad: formData.transportistaCiudad,
          codigoPostal: formData.transportistaCP,
          telefono: formData.transportistaTelefono,
          email: formData.transportistaEmail,
          licencia: formData.transportistaLicencia
        },
        origen: {
          direccion: formData.origenDireccion,
          ciudad: formData.origenCiudad,
          codigoPostal: formData.origenCP
        },
        destino: {
          direccion: formData.destinoDireccion,
          ciudad: formData.destinoCiudad,
          codigoPostal: formData.destinoCP
        },
        transporte: {
          fechaRecogida: formData.fechaRecogida,
          horaRecogida: formData.horaRecogida,
          fechaEntrega: formData.fechaEntrega,
          horaEntrega: formData.horaEntrega
        },
        mercancia: {
          descripcion: formData.descripcionMercancia,
          peso: formData.peso,
          volumen: formData.volumen,
          bultos: formData.bultos,
          observaciones: formData.observacionesMercancia
        },
        vehiculo: vehiculoData,
        estado: 'generado',
        urlVerificacion: `${window.location.origin}/verificar-deca/${decaId}`
      };

      // Guardar en Firestore
      console.log('💾 Guardando DECA en Firestore...', { decaId, effectiveCompanyId });
      await setDoc(doc(collection(db, 'decas'), decaId), decaData);
      console.log('✅ DECA guardado en Firestore correctamente');

      // Generar PDF
      console.log('📄 Iniciando generación de PDF...');
      const response = await fetch(`https://us-central1-firma-digital-db89b.cloudfunctions.net/generateDECAPdf`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({ decaData, decaId })
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error generando PDF:', errorText);
        throw new Error(`Error al generar el PDF: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ PDF generado correctamente:', result);
      
      // Clear saved form data on success
      try {
        localStorage.removeItem('deca_form_data');
        console.log('🗑️ Datos del formulario limpiados tras éxito');
      } catch (error) {
        console.warn('Error clearing form data:', error);
      }
      
      showToast('DECA generado correctamente', 'success');
      navigate(`/verificar-deca/${decaId}`);

    } catch (error) {
      console.error('❌ Error generando DECA:', error);
      
      // Better error messages based on error type
      let errorMessage = error.message || 'Error al generar DECA';
      
      if (error.code === 'permission-denied' || error.message?.includes('insufficient permissions')) {
        errorMessage = 'Error de permisos. Inicia sesión nuevamente y vuelve a intentar.';
        console.log('🔄 Sugiriendo re-login por error de permisos');
      } else if (error.code === 'unauthenticated') {
        errorMessage = 'Sesión expirada. Redirigiendo al login...';
        setTimeout(() => navigate('/login'), 2000);
      }
      
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="container">
      <div className="header">
        <span className="badge">DECA</span>
      </div>

      <h1 className="title">Generar Documento DECA</h1>
      
      <div style={{ marginBottom: 16, padding: "12px 16px", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: "8px", fontSize: "14px", color: "#92400e" }}>
        Documento Electrónico de Control Administrativo - Obligatorio desde 5 Oct 2026
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* SECCIÓN CARGADOR */}
          <div style={{ background: "#dbeafe", padding: "16px", borderRadius: "8px", border: "1px solid #bfdbfe" }}>
            <h3 style={{ color: "#1e40af", margin: "0 0 16px 0", fontWeight: 600, fontSize: "16px" }}>
              Datos del Cargador (Quien Envía)
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px" }}>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>
                  Nombre/Razón Social *
                </label>
                <input
                  type="text"
                  name="cargadorNombre"
                  value={formData.cargadorNombre}
                  onChange={handleInputChange}
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                  required
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>
                  CIF/NIF *
                </label>
                <input
                  type="text"
                  name="cargadorCIF"
                  value={formData.cargadorCIF}
                  onChange={handleInputChange}
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                  required
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>
                  Dirección
                </label>
                <input
                  type="text"
                  name="cargadorDireccion"
                  value={formData.cargadorDireccion}
                  onChange={handleInputChange}
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Ciudad</label>
                  <input
                    type="text"
                    name="cargadorCiudad"
                    value={formData.cargadorCiudad}
                    onChange={handleInputChange}
                    style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>CP</label>
                  <input
                    type="text"
                    name="cargadorCP"
                    value={formData.cargadorCP}
                    onChange={handleInputChange}
                    style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Teléfono</label>
                <input
                  type="tel"
                  name="cargadorTelefono"
                  value={formData.cargadorTelefono}
                  onChange={handleInputChange}
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Email</label>
                <input
                  type="email"
                  name="cargadorEmail"
                  value={formData.cargadorEmail}
                  onChange={handleInputChange}
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                />
              </div>
            </div>
          </div>

          {/* SECCIÓN TRANSPORTISTA */}
          <div style={{ background: "#dcfce7", padding: "16px", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
            <h3 style={{ color: "#166534", margin: "0 0 16px 0", fontWeight: 600, fontSize: "16px" }}>
              Datos del Transportista
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px" }}>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>
                  Nombre/Razón Social *
                </label>
                <input
                  type="text"
                  name="transportistaNombre"
                  value={formData.transportistaNombre}
                  onChange={handleInputChange}
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                  required
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>
                  CIF/NIF *
                </label>
                <input
                  type="text"
                  name="transportistaCIF"
                  value={formData.transportistaCIF}
                  onChange={handleInputChange}
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                  required
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Licencia Transporte</label>
                <input
                  type="text"
                  name="transportistaLicencia"
                  value={formData.transportistaLicencia}
                  onChange={handleInputChange}
                  placeholder="Ej: MDL-123456789"
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Email</label>
                <input
                  type="email"
                  name="transportistaEmail"
                  value={formData.transportistaEmail}
                  onChange={handleInputChange}
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                />
              </div>
            </div>
          </div>
          {/* SECCIÓN ORIGEN Y DESTINO */}
          <div style={{ background: "#f3e8ff", padding: "16px", borderRadius: "8px", border: "1px solid #d8b4fe" }}>
            <h3 style={{ color: "#7c2d92", margin: "0 0 16px 0", fontWeight: 600, fontSize: "16px" }}>
              Origen y Destino
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "24px" }}>
              {/* ORIGEN */}
              <div>
                <h4 style={{ fontWeight: 600, color: "#7c2d92", borderBottom: "2px solid #d8b4fe", paddingBottom: "8px", marginBottom: "16px" }}>
                  Lugar de Recogida
                </h4>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Dirección *</label>
                    <input
                      type="text"
                      name="origenDireccion"
                      value={formData.origenDireccion}
                      onChange={handleInputChange}
                      style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                      required
                    />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "12px" }}>
                    <div>
                      <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Ciudad *</label>
                      <input
                        type="text"
                        name="origenCiudad"
                        value={formData.origenCiudad}
                        onChange={handleInputChange}
                        style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>CP *</label>
                      <input
                        type="text"
                        name="origenCP"
                        value={formData.origenCP}
                        onChange={handleInputChange}
                        style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* DESTINO */}
              <div>
                <h4 style={{ fontWeight: 600, color: "#7c2d92", borderBottom: "2px solid #d8b4fe", paddingBottom: "8px", marginBottom: "16px" }}>
                  Lugar de Entrega
                </h4>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Dirección *</label>
                    <input
                      type="text"
                      name="destinoDireccion"
                      value={formData.destinoDireccion}
                      onChange={handleInputChange}
                      style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                      required
                    />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "12px" }}>
                    <div>
                      <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Ciudad *</label>
                      <input
                        type="text"
                        name="destinoCiudad"
                        value={formData.destinoCiudad}
                        onChange={handleInputChange}
                        style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                        required
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>CP *</label>
                      <input
                        type="text"
                        name="destinoCP"
                        value={formData.destinoCP}
                        onChange={handleInputChange}
                        style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SECCIÓN FECHAS */}
          <div style={{ background: "#fed7aa", padding: "16px", borderRadius: "8px", border: "1px solid #fdba74" }}>
            <h3 style={{ color: "#ea580c", margin: "0 0 16px 0", fontWeight: 600, fontSize: "16px" }}>
              Fechas del Transporte
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
              <div>
                <h4 style={{ fontWeight: 600, color: "#ea580c", marginBottom: "16px" }}>Recogida</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Fecha</label>
                    <input
                      type="date"
                      name="fechaRecogida"
                      value={formData.fechaRecogida}
                      onChange={handleInputChange}
                      style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Hora</label>
                    <input
                      type="time"
                      name="horaRecogida"
                      value={formData.horaRecogida}
                      onChange={handleInputChange}
                      style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                    />
                  </div>
                </div>
              </div>
              <div>
                <h4 style={{ fontWeight: 600, color: "#ea580c", marginBottom: "16px" }}>Entrega</h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Fecha</label>
                    <input
                      type="date"
                      name="fechaEntrega"
                      value={formData.fechaEntrega}
                      onChange={handleInputChange}
                      style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Hora</label>
                    <input
                      type="time"
                      name="horaEntrega"
                      value={formData.horaEntrega}
                      onChange={handleInputChange}
                      style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* SECCIÓN MERCANCÍA */}
          <div style={{ background: "#fee2e2", padding: "16px", borderRadius: "8px", border: "1px solid #fecaca" }}>
            <h3 style={{ color: "#dc2626", margin: "0 0 16px 0", fontWeight: 600, fontSize: "16px" }}>
              Datos de la Mercancía
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>
                  Descripción de la Mercancía *
                </label>
                <textarea
                  name="descripcionMercancia"
                  value={formData.descripcionMercancia}
                  onChange={handleInputChange}
                  rows="3"
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
                  placeholder="Ej: Cuadros eléctricos industriales..."
                  required
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Peso (Kg)</label>
                  <input
                    type="number"
                    name="peso"
                    value={formData.peso}
                    onChange={handleInputChange}
                    step="0.01"
                    placeholder="0.00"
                    style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Volumen (m³)</label>
                  <input
                    type="number"
                    name="volumen"
                    value={formData.volumen}
                    onChange={handleInputChange}
                    step="0.01"
                    placeholder="0.00"
                    style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Número de Bultos</label>
                  <input
                    type="number"
                    name="bultos"
                    value={formData.bultos}
                    onChange={handleInputChange}
                    placeholder="0"
                    style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Observaciones</label>
                <textarea
                  name="observacionesMercancia"
                  value={formData.observacionesMercancia}
                  onChange={handleInputChange}
                  rows="2"
                  style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
                  placeholder="Observaciones adicionales sobre la mercancía..."
                />
              </div>
            </div>
          </div>
          {/* SECCIÓN VEHÍCULO Y CONDUCTOR */}
          <div style={{ background: "#f9fafb", padding: "16px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
            <h3 style={{ color: "#374151", margin: "0 0 16px 0", fontWeight: 600, fontSize: "16px" }}>
              Datos del Vehículo y Conductor
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "24px" }}>
              {/* VEHÍCULO */}
              <div>
                <h4 style={{ fontWeight: 600, color: "#374151", borderBottom: "2px solid #e5e7eb", paddingBottom: "8px", marginBottom: "16px" }}>
                  Vehículo
                </h4>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Matrícula *</label>
                    <input
                      type="text"
                      name="matricula"
                      value={vehiculoData.matricula}
                      onChange={handleVehiculoChange}
                      placeholder="Ej: 1234ABC"
                      style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                      required
                    />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                    <div>
                      <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Marca</label>
                      <input
                        type="text"
                        name="marca"
                        value={vehiculoData.marca}
                        onChange={handleVehiculoChange}
                        style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Modelo</label>
                      <input
                        type="text"
                        name="modelo"
                        value={vehiculoData.modelo}
                        onChange={handleVehiculoChange}
                        style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {/* CONDUCTOR */}
              <div>
                <h4 style={{ fontWeight: 600, color: "#374151", borderBottom: "2px solid #e5e7eb", paddingBottom: "8px", marginBottom: "16px" }}>
                  Conductor
                </h4>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Nombre Completo *</label>
                    <input
                      type="text"
                      name="conductorNombre"
                      value={vehiculoData.conductorNombre}
                      onChange={handleVehiculoChange}
                      style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>DNI/NIE</label>
                    <input
                      type="text"
                      name="conductorDNI"
                      value={vehiculoData.conductorDNI}
                      onChange={handleVehiculoChange}
                      placeholder="Ej: 12345678A"
                      style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "6px" }}>Teléfono</label>
                    <input
                      type="tel"
                      name="conductorTelefono"
                      value={vehiculoData.conductorTelefono}
                      onChange={handleVehiculoChange}
                      style={{ width: "100%", background: '#fff', border: '1px solid #d1d5db', borderRadius: 10, padding: '9px 12px', color: '#111827', outline: 'none', fontSize: 14 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* BOTONES */}
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingTop: "24px", borderTop: "2px solid #e5e7eb" }}>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="copy"
                style={{ flex: "1", minWidth: "120px", padding: "12px 24px", fontSize: "14px" }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (confirm('¿Estás seguro de que quieres limpiar todos los datos del formulario?')) {
                    setFormData({
                      cargadorNombre: "", cargadorCIF: "", cargadorDireccion: "", cargadorCiudad: "", cargadorCP: "", cargadorTelefono: "", cargadorEmail: "",
                      transportistaNombre: "", transportistaCIF: "", transportistaDireccion: "", transportistaCiudad: "", transportistaCP: "", transportistaTelefono: "", transportistaEmail: "", transportistaLicencia: "",
                      origenDireccion: "", origenCiudad: "", origenCP: "", destinoDireccion: "", destinoCiudad: "", destinoCP: "",
                      fechaRecogida: "", horaRecogida: "", fechaEntrega: "", horaEntrega: "",
                      descripcionMercancia: "", peso: "", volumen: "", bultos: "", observacionesMercancia: ""
                    });
                    setVehiculoData({ matricula: "", marca: "", modelo: "", conductorNombre: "", conductorDNI: "", conductorTelefono: "" });
                    localStorage.removeItem('deca_form_data');
                    showToast('Formulario limpiado', 'success');
                  }
                }}
                className="copy"
                style={{ flex: "1", minWidth: "120px", padding: "12px 24px", fontSize: "14px", background: "#f59e0b", color: "#fff", border: "none" }}
              >
                Limpiar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="copy"
                style={{ 
                  flex: "2", 
                  minWidth: "200px", 
                  padding: "12px 24px", 
                  fontSize: "14px",
                  background: loading ? "#e5e7eb" : "#3b82f6",
                  color: loading ? "#9ca3af" : "#fff",
                  border: "none",
                  cursor: loading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px"
                }}
              >
                {loading ? (
                  <>
                    <div className="spinner-small"></div>
                    Generando DECA...
                  </>
                ) : (
                  "Generar Documento DECA"
                )}
              </button>
            </div>
          </div>
        </form>
        {/* INFORMACIÓN LEGAL */}
        <div style={{ marginTop: "24px", padding: "16px", background: "#fef3c7", border: "1px solid #fde68a", borderRadius: "8px" }}>
          <h4 style={{ fontWeight: 600, color: "#92400e", marginBottom: "12px", fontSize: "16px" }}>Información Legal</h4>
          <ul style={{ fontSize: "14px", color: "#92400e", margin: 0, paddingLeft: "20px", lineHeight: "1.5" }}>
            <li>El DECA es obligatorio desde el 5 de octubre de 2026 para transporte interior</li>
            <li>Debe incluir código QR con URL de verificación pública</li>
            <li>Es válido para transportes con origen y destino en España</li>
            <li>Para transportes internacionales usar carta de porte CMR</li>
            <li>El documento se genera en formato PDF con firma digital</li>
          </ul>
        </div>
      </div>
    </div>
  );
}